import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { api } from "./ApiClient";

import { CookiesHelper } from "../helpers/cookies.helper";

import { HMICreateConversation, HMICompleteConversation, IConversationForCompleteData, IConversationForSimulationReport } from "../types/services.interface";
import { EtyConversation } from "../types/entities.interface";
import { ESimulationKey } from "../types/entities.enum";

export const useCreateConversation = () => {
    const navigate = useNavigate();

    const mutationFn = async (data: HMICreateConversation): Promise<EtyConversation> => {
        const token = CookiesHelper.get(`${process.env.REACT_APP__SESSION_TOKEN_NAME}`);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const res = await api.post('/conversations', data.payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsProcessing(true);
        },
        onSuccess: (data, variables) => {
            const { simulation_key, stage_key } = variables.payload;
            navigate(`/simulation/${simulation_key}/stages/${stage_key}/conversation/${data.id}`);
        },
        onError: (error: any) => {
            window.alert(error?.response?.data?.message || 'Something went wrong.');
        },
        onSettled: (_, __, variables) => {
            variables.setIsProcessing(false);
        },
    });
};

export const useCompleteConversation = () => {
    const navigate = useNavigate();

    const mutationFn = async (data: HMICompleteConversation): Promise<void> => {
        const token = CookiesHelper.get(`${process.env.REACT_APP__SESSION_TOKEN_NAME}`);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        await api.patch(`/conversations/complete/${data.conversationId}`);
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsProcessing(true);
        },
        onSuccess: (_, variables) => {
            if (variables.nextOperation === 'next-persona') {
                if (variables.nextStageDetails) {
                    navigate(`/simulation/${variables.simulationKey}/stages/${variables.nextStageDetails.stageKey}/details/${variables.nextStageDetails.action}`);
                } else {
                    navigate(`/simulation/${variables.simulationKey}/stages`);
                };
            } else {
                navigate(`/simulation/${variables.simulationKey}/analysis`);
            };
        },
        onError: (error: any) => {
            window.alert(error?.response?.data?.message || 'Something went wrong.');
        },
        onSettled: (_, __, variables) => {
            variables.setIsProcessing(false);
        },
    });
};

export const useGetConversationForCompleteData = (conversationId: string | null) => {
    const queryFn = async (): Promise<IConversationForCompleteData> => {
        const res = await api.get(`/conversations/complete-data/${conversationId}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['conversation-for-complete-data', conversationId],
        queryFn,
        retry: false,
        enabled: !!conversationId,
    });
};

export const useGetConversationsForSimulationReport = (simulationKey: ESimulationKey | null) => {
    const queryFn = async (): Promise<IConversationForSimulationReport[]> => {
        const token = CookiesHelper.get(`${process.env.REACT_APP__SESSION_TOKEN_NAME}`);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const res = await api.get(`/conversations/simulation-report/${simulationKey}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['simulation-report-conversations', simulationKey],
        queryFn,
        retry: false,
        enabled: !!simulationKey,
    });
};
